import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RecordActionsMenu, RecordBadges } from "@/components/RecordActions";
import { FolderOpen, Download, Eye, Package, CheckCircle2, AlertTriangle, Loader2, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = {
  "Draft Generated": "bg-blue-100 text-blue-800", "Internal Review": "bg-indigo-100 text-indigo-800",
  "Needs Revision": "bg-amber-100 text-amber-800", "Customer Review": "bg-purple-100 text-purple-800",
  "Awaiting Approval": "bg-orange-100 text-orange-800", "Approved": "bg-emerald-100 text-emerald-800",
  "Superseded": "bg-slate-200 text-slate-600", "Archived": "bg-slate-100 text-slate-500", "Entered in Error": "bg-red-100 text-red-700",
};
const WORKFLOW = ["Internal Review", "Needs Revision", "Customer Review", "Awaiting Approval", "Approved"];

export default function CustomerDocuments() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [approve, setApprove] = useState(null);

  useEffect(() => {
    api.get("/recon/reconciliations").then((r) => {
      const m = {}; r.data.forEach((x) => { m[x.customer_id] = x.customer_name; });
      setCustomers(Object.entries(m).map(([id, name]) => ({ id, name })));
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const p = {};
    if (customerId !== "all") p.customer_id = customerId;
    if (statusF !== "all") p.status = statusF;
    api.get("/policy/documents", { params: p }).then((r) => setDocs(r.data.documents || [])).catch(() => {});
  }, [customerId, statusF]);
  useEffect(() => { load(); }, [load]);

  const cname = (id) => customers.find((c) => c.id === id)?.name || id?.slice(0, 8);

  const download = (d) => { window.open(`${API}/api/policy/documents/${d.id}/download`, "_blank"); setTimeout(load, 800); };
  const setStatus = async (d, status) => {
    try { await api.post(`/policy/documents/${d.id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportPkg = async (kind) => {
    if (customerId === "all") { toast.error("Select a customer to export a package."); return; }
    try {
      const res = await api.post("/policy/export", { customer_id: customerId, kind }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${cname(customerId)}_Policy_Package.zip`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2"><FolderOpen className="h-6 w-6 text-gold" /> Customer Documents</h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Generated customer-specific policies and procedures. Review the DOCX, advance through the approval workflow, and export policy packages.</p>
      </header>

      <div className="p-6 md:p-8 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 space-y-1"><Label className="text-xs">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}><SelectTrigger data-testid="doc-customer-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All customers</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="w-48 space-y-1"><Label className="text-xs">Status</Label>
            <Select value={statusF} onValueChange={setStatusF}><SelectTrigger data-testid="doc-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.keys(STATUS_STYLES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => exportPkg("approved")} data-testid="doc-export-approved"><Package className="h-4 w-4 mr-1.5" /> Export Approved</Button>
          <Button variant="outline" size="sm" onClick={() => exportPkg("full")} data-testid="doc-export-full"><Package className="h-4 w-4 mr-1.5" /> Export Full Package</Button>
        </div>

        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left">{["Document", "Customer", "Type", "Ver", "Tpl", "Status", "Owner", "Approver", "Effective", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {docs.length === 0 ? <tr><td colSpan={10} className="px-3 py-10 text-center text-muted-foreground text-sm">No generated documents. Use the Policy &amp; Procedure Generator to create documents.</td></tr> :
                docs.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid="doc-row">
                    <td className="px-3 py-2.5 font-medium text-navy"><div className="flex items-center gap-1.5">{d.title}<RecordBadges classification={d.classification} lifecycleState={d.lifecycle_state} /></div>{d.unresolved?.length ? <span className="text-[10px] text-amber-700">{d.unresolved.length} unresolved</span> : null}</td>
                    <td className="px-3 py-2.5 text-xs">{cname(d.customer_id)}</td>
                    <td className="px-3 py-2.5 text-xs">{d.doc_type}</td>
                    <td className="px-3 py-2.5 text-xs">{d.version}</td>
                    <td className="px-3 py-2.5 text-xs">{d.template_version}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[d.status] || "bg-slate-100"}`}>{d.status}</span></td>
                    <td className="px-3 py-2.5 text-xs">{d.owner || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{d.approver || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{d.effective_date || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-0.5">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Preview" onClick={() => setPreview(d)} data-testid={`doc-preview-${d.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Download DOCX" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                        <Select value="" onValueChange={(v) => v === "Approved" ? setApprove(d) : setStatus(d, v)}>
                          <SelectTrigger className="h-8 w-8 p-0 border-0 [&>svg]:hidden justify-center" data-testid={`doc-workflow-${d.id}`}><span className="text-xs">⋯</span></SelectTrigger>
                          <SelectContent>{WORKFLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <RecordActionsMenu type="policy_document" id={d.id} onChanged={load} />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table></div>
        </div>
      </div>

      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
      {approve && <ApproveDialog doc={approve} onClose={() => setApprove(null)} onDone={() => { setApprove(null); load(); }} />}
    </div>
  );
}

function PreviewDialog({ doc, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/policy/documents/${doc.id}/preview`).then((r) => setData(r.data)).catch(() => {}); }, [doc.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="doc-preview-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-gold" /> {doc.title} · v{doc.version}</DialogTitle>
          <DialogDescription className="text-amber-700 flex items-start gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Content preview only — page breaks, headers, footers, spacing, fonts and pagination may differ from the actual DOCX. Download and inspect the DOCX before approval.</DialogDescription></DialogHeader>
        {!data ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <div className="space-y-2 text-sm bg-white border border-border rounded p-5">
            {data.blocks.map((b, i) => b.type === "table" ? (
              <table key={i} className="w-full border border-border text-xs my-2"><tbody>{b.rows.map((row, ri) => <tr key={ri} className="border-b border-border">{row.map((c, ci) => <td key={ci} className="border-r border-border px-2 py-1">{c}</td>)}</tr>)}</tbody></table>
            ) : (
              <p key={i} className={b.type === "heading" ? "font-bold text-navy mt-3" : "text-slate-700"}>{b.text}</p>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => window.open(`${API}/api/policy/documents/${doc.id}/download`, "_blank")}><Download className="h-4 w-4 mr-1.5" /> Download DOCX</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDialog({ doc, onClose, onDone }) {
  const [f, setF] = useState({ owner: doc.owner || "", approver: doc.approver || "", effective_date: doc.effective_date || "", review_date: doc.review_date || "", internal_reviewer_ok: doc.internal_reviewer_ok || false, customer_approved: doc.customer_approved || false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/policy/documents/${doc.id}/status`, { status: "Approved", ...f }); toast.success("Document approved."); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approval blocked"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="doc-approve-dialog">
        <DialogHeader><DialogTitle>Approve {doc.title}</DialogTitle>
          <DialogDescription>Approval requires the DOCX to be downloaded/inspected, no unresolved required fields, no raw placeholders, and owner/approver/version/effective date/review date set.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          {doc.unresolved?.length > 0 && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{doc.unresolved.length} unresolved required field(s) — resolve and regenerate before approval.</div>}
          {!doc.downloaded && <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Download the DOCX first — approval is blocked until it is inspected.</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Owner *</Label><Input value={f.owner} onChange={(e) => set("owner", e.target.value)} data-testid="approve-owner" /></div>
            <div className="space-y-1"><Label className="text-xs">Approver *</Label><Input value={f.approver} onChange={(e) => set("approver", e.target.value)} data-testid="approve-approver" /></div>
            <div className="space-y-1"><Label className="text-xs">Effective date *</Label><Input type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid="approve-effective" /></div>
            <div className="space-y-1"><Label className="text-xs">Review date *</Label><Input type="date" value={f.review_date} onChange={(e) => set("review_date", e.target.value)} data-testid="approve-review" /></div>
          </div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.internal_reviewer_ok} onChange={(e) => set("internal_reviewer_ok", e.target.checked)} data-testid="approve-reviewer-ok" /> Internal reviewer has confirmed the document</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.customer_approved} onChange={(e) => set("customer_approved", e.target.checked)} /> Customer approval recorded (where required)</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy} onClick={submit} data-testid="approve-confirm">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Approve</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
